<template>
  <div class="report-header">
    <div class="report-banner">
      <div class="logo">
        <img :src="`${$STATIC_PATH}/img/DOE_logo.png`" alt="NYC DOE"/>
      </div>
      <div class="report-title">
        <h1>{{ schoolYearRange }} School Quality Snapshot&nbsp;<span class="title-beta">BETA</span></h1>
        <p class="school-type">{{ schoolType }}</p>
        <ButtonLogout v-if="isEmbargoed && !hasSelectedSchool" class="header-button"/>
      </div>
    </div>
    <div class="selectors-row shadowed-bar no-print">
      <div>
        <label for="select-school"><template v-if="!hasSelectedSchool">Choose a </template>School:</label>
        <FuzzySearchbox
          id="select-school"
          aria-label="select a school"
          placeholder="Type here to search for a school"
          v-model="selectedSchoolOption"
          :preSelectedItem="selectedSchool"
          @change="schoolOptionChanged"
          :items="selectSchoolList"
          :item-keys="['school', 'report_type', 'dbn']"
          :searchByLabel = "true"
          :searchByLabelRuleFunction = "searchStringForSchool"
          :create-label-function="displayStringForSchool">
        </FuzzySearchbox>
      </div>
      <InputSelectYear :report-code="reportCode" :report-year="reportYear" :year-label="hasSelectedSchool?undefined:'Choose a Year'"/>
      <div id="google_translate_placeholder" ref="google-translate"></div>
    </div>
    <div v-if="selectedSchoolDisplayName!=''" id="below-selectors">
      <h2 class="school-name skiptranslate notranslate">{{selectedSchoolDisplayName}}</h2>
      <ButtonLogout v-if="isEmbargoed" class="header-button"/>
      <a id="print-button" class="header-button no-print" v-on:click="print" href="javascript:"> Print </a>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Component, { mixins } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import pick from 'lodash/pick'
import FuzzySearchbox from 'fuzzy-searchbox'

import { schoolTypeDisplayForType, schoolTypeCodeForName,
  dbnReportTypeForSchool, schoolsAreEqual } from '@/utils/data'
import { devOnlyLog } from '@/utils/log'
import { logEvent } from '@/helpers/google-analytics'
import { REPORT_CODE, REPORT_YEAR, NS_REPORT_GROUP, NS_CURRENT_REPORT } from './const'

import { RouteMixin } from '@/mixin'
import ButtonLogout from '@/components/ButtonLogout'
import InputSelectYear from '@/components/InputSelectYear'

@Component({
  computed: {
    ...mapGetters(NS_REPORT_GROUP, [
      'routeSchool',
      'schoolYearRange'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, [
      'isEmbargoed',
      'isSchoolInfoLoaded',
      'selectedSchool',
      'hasSelectedSchool',
      'selectedSchoolDisplayName'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, {
      selectSchoolList: 'schoolListAuthorized'
    })
  },
  methods: {
    ...mapActions(NS_CURRENT_REPORT, [
      'updateSelectedSchoolData'
    ]),
    ...mapMutations(NS_CURRENT_REPORT, [
      'setSelectedSchool',
      'clearSelectedSchool',
      'clearSelectedSchoolData'
    ])
  },
  components: {
    ButtonLogout,
    InputSelectYear,
    FuzzySearchbox
  }
})
export default class ReportHeader extends mixins(RouteMixin) {
  reportCode = REPORT_CODE
  reportYear = REPORT_YEAR

  selectedSchoolOption = ''

  /* Lifecycle */

  mounted () {
    this.$refs['google-translate'].appendChild(this.$TRANSLATE_ELEMENT)
    this.$TRANSLATE_ELEMENT.style.display = 'inherit'
    this.updateSelectedSchoolForRoute()
  }

  /* Watchers */

  @Watch('selectSchoolList')
  onSchoolListLoaded (newVal, oldVal) {
    this.updateSelectedSchoolForRoute()
    this.updateAutoSelectSchool()
  }
  @Watch('routeSchool')
  onRouteSchoolChanged (newVal, oldVal) {
    this.updateSelectedSchoolForRoute()
  }

  /* Getters */

  get schoolType () {
    return this.selectedSchool ? schoolTypeDisplayForType(this.selectedSchool.school_type) : ''
  }

  /* Methods - utility */

  dbnRTForSchool (s) {
    return dbnReportTypeForSchool(s)
  }
  optionValueForSchool (s) {
    if (!s || !s.dbn || !s.report_type) return ''
    return JSON.stringify(pick(s, ['dbn', 'report_type']))
  }
  displayStringForSchool (s) {
    const st = schoolTypeCodeForName(s.school_type)
    return `${s.school} - [${st}] ${s.dbn}`
  }
  removeFirstLeadingZeros (string) {
    return string.replace(/\b0+/, '')
  }
  searchStringForSchool (string) {
    var dbn = string.slice(string.lastIndexOf(' ') + 1)
    string = this.removeFirstLeadingZeros(string)
    string = string.replace(/[^\w\s[\]-]|_|-\s/g, '').replace(/\s+/g, ' ')
    string = `${dbn} ${string}`
    return string
  }

  /* Methods - event handlers */

  schoolOptionChanged () {
    this.updateSelectedSchool(this.selectedSchoolOption)
  }

  print () {
    logEvent(this, 'Link', 'pageLinkClicked', 'print')
    window.print()
  }

  /* Methods - updating selected school / school list */

  clearSelectedSchoolAndData () {
    this.selectedSchoolOption = ''
    this.clearSelectedSchool()
    this.clearSelectedSchoolData()
    this.clearRouteSchool()
  }

  clearRouteSchool () {
    this.updateRouteForSchool(null)
  }

  // For programmatically changing dropdown selected school
  updateSelectedSchoolOption (s) {
    this.selectedSchoolOption = this.optionValueForSchool(s)
    this.updateSelectedSchool(s)
  }

  updateRouteForSchool (s) {
    const newSchool = s || {}
    const newRoute = this.$_RouteMixin_getRouteCopy()
    if (schoolsAreEqual(newRoute.params, newSchool)) return

    // Update the displayed route in URL bar
    newRoute.params = pick(newSchool, ['dbn', 'report_type'])
    this.$_RouteMixin_setRoute(newRoute)
  }

  updateSelectedSchool (s) {
    if (!s || !s.dbn || !s.report_type) {
      this.clearSelectedSchoolAndData()
      return
    }

    const schoolList = this.selectSchoolList
    if (schoolList === undefined) {
      // School lists not loaded yet, or if embargoed, user not logged in yet
      return
    }

    const match = schoolList.filter((d) => schoolsAreEqual(d, s))
    if (match.length !== 1) {
      this.clearSelectedSchoolAndData()
      devOnlyLog('ERROR: schoolOptionChanged - invalid school selected')
      return
    }

    const fullSchool = match[0]
    this.setSelectedSchool(fullSchool)
    this.updateSelectedSchoolData({
      year: REPORT_YEAR,
      dbn: s.dbn,
      reportType: s.report_type
    })
    this.updateRouteForSchool(s)
  }

  updateSelectedSchoolForRoute () {
    // Need to wait for school lists to be loaded before trying to populate the
    //  full school and update school select dropdown
    if (!this.isSchoolInfoLoaded) return

    // Skip if school has already been updated (when displayed route is updated
    //  after user selects a school)
    const s = this.routeSchool
    if (schoolsAreEqual(s, this.selectedSchool)) return

    this.updateSelectedSchoolOption(s)
  }

  updateAutoSelectSchool () {
    if (this.selectSchoolList && this.selectSchoolList.length === 1) {
      const s = this.selectSchoolList[0]
      this.updateSelectedSchoolOption(s)
    }
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import 'src/styles/const.scss';
@import './css/const.scss';
.report-header {
  margin-bottom: 20px;
}
.report-banner {
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
  padding-bottom: 16px;
}
.logo {
  height: 54px;
  > img {
    height: 100%;
  }
}
.report-title {
  text-align: right;
  > * {
    margin: 0px;
  }
  h1 {
    font-size: 24px;
  }
  .school-type {
    font-size: 18px;
    margin-top: 4px;
  }
}
.title-beta {
  font-size: 14px;
  font-weight: bold;
}
.selectors-row {
  > div {
    display: inline-block;
    padding-right: 20px;
  }
  padding-top: 12px;
  position: relative;
  padding-bottom: 12px;
  /deep/ label {
    font-size: 12px;
    font-weight: bold;
  }
}
#select-school {
  width: 400px;
}
#google_translate_placeholder {
  padding-right: 0px;
  position: absolute;
  top: 0px;
  right: 10px;
  /deep/ .goog-te-gadget {
    color: #555;
  }
}
.no-selected-school .selectors-row {
  display: block;
  padding-top: 48px;
  padding-bottom: 48px;
  > div {
    padding: 12px 0px;
  }
  /deep/ label {
    width: 210px;
    text-align: right;
    display: inline-block;
    padding-right: 16px;
  }
  /deep/ input,
  /deep/ label,
  /deep/ select, {
    font-size: 20px;
  }
  #google_translate_placeholder {
    float: right;
    display: block;
    position:relative;
  }
}
.report-banner, .selectors-row {
  @media screen and (max-width: $small-screen-width) {
    flex-flow: column;
  }
}
.school-name {
  font-size: 28px;
  font-weight: bold;
  padding-top: 0px;
  padding-bottom: 0px;
  max-height: 64px;
  margin-bottom: 0px;
  @media print {
    margin-top: 8px;
  }
}

#below-selectors {
  display: flex;
  justify-content: space-between;
  h2 {
    flex: 1;
  }
}

a.header-button {
  text-decoration: none;
  display: inline-block;
  height: 20px;
  margin-top: 24px;
  margin-bottom: 10px;
  margin-left: 10px;
  padding-left: 5px;
  padding-right: 5px;
  padding-top: 2px;
  background-color: #eeeeee;
  border: 1px solid #c4c4c4;
  color:black;
  border-radius: 4px;
}
.report-title .button-logout {
  margin-top: 10px;
  margin-bottom: 0px;
}

/* Responsive rules */

@media screen and (max-width: $screen-width-2-column-large) {
.school-name {
  max-height: none;
}
}
@media screen and (max-width: $screen-width-2-column-small) {
.report-banner {
  display: block;
}
.report-title {
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
}
.report-title .button-logout {
  margin-top: 2px;
}
.selectors-row {
  > div {
    padding-right: 10px;
  }
  /deep/ label {
    display: none;
  }
}
}
@media screen and (max-width: $medium-screen-width) {
#select-school {
  width: 320px;
}
}
@media screen and (max-width: $screen-width-1-column-large) {
.logo {
  height: 40px;
}
.report-title {
  display: block;
  text-align: left;
}
.report-title .button-logout {
  margin-left: 0px;
}
#select-school {
  width: 270px;
}
#print-button {
  display: none;
}
.no-selected-school .selectors-row {
  #google_translate_placeholder {
    float: none;
    display: block;
    position:relative;
    margin-left: 230px;
    top: 0px;
    right: 0px;
  }
}
}
@media screen and (max-width: $screen-width-1-column-medium) {
#select-school {
  width: 350px;
}
#google_translate_placeholder {
  float: right;
  padding-right: 0px;
  position: absolute;
  top: 50px;
  right: 0px;
}
.selectors-row {
  margin-bottom: 30px;
}
.selectors-row > div {
  padding-right: 2px;
}
.report:not(.no-selected-school) .selectors-row {
  /deep/ #google_translate_placeholder .goog-te-menu-value {
    &::before {
      content: "Language";
    }
    span:first-child {
      display: none;
    }
  }
}
.no-selected-school .selectors-row {
  > div {
    display: block;
  }
  #select-school {
    width: 100%;
  }
  /deep/ label {
    text-align: left;
    display: block;
  }
  #google_translate_placeholder {
    margin-left: 0px;
  }
}
}
@media screen and (max-width: $screen-width-1-column-small) {
#select-school {
  width: 300px;
}
#google_translate_placeholder {
    top: 75px;
}
.input-select-year {
  margin-right: 60px;
}
}
</style>



// WEBPACK FOOTER //
// src/components/sqs/2017/ReportHeader.vue